import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Card } from "./ui/card";
import { Sparkles, Send, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

/**
 * "Pergunte ao Job" — AI chat that answers questions using this job's
 * communication history (messages, voice transcripts, photo captions,
 * issues/tasks). Answers include tappable citations that jump straight to
 * the original message (/chat/{cid}?msg={mid}).
 */
export function AskJob({ jobId }) {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState([]); // {role, content, citations?}
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, loading]);

  const ask = async () => {
    const q = question.trim();
    if (!q || loading) return;
    setQuestion("");
    const history = turns.map(({ role, content }) => ({ role, content }));
    setTurns((prev) => [...prev, { role: "user", content: q }]);
    setLoading(true);
    try {
      const res = await api.post(`/jobs/${jobId}/ask`, { user_id: user.id, question: q, history });
      setTurns((prev) => [...prev, { role: "assistant", content: res.data.answer, citations: res.data.citations || [] }]);
    } catch (e) {
      toast.error(t("ask_job_error"));
      setTurns((prev) => prev.slice(0, -1));
      setQuestion(q);
    } finally {
      setLoading(false);
    }
  };

  // Render the answer replacing [n] markers with tappable citation pills
  const renderAnswer = (turn) => {
    const byN = {};
    (turn.citations || []).forEach((c) => { byN[c.n] = c; });
    const parts = String(turn.content || "").split(/(\[\d{1,3}\])/g);
    return parts.map((p, i) => {
      const m = p.match(/^\[(\d{1,3})\]$/);
      if (m && byN[Number(m[1])]) {
        const c = byN[Number(m[1])];
        return (
          <button
            key={i}
            type="button"
            data-testid={`ask-job-citation-${c.n}`}
            onClick={() => navigate(`/chat/${c.conversation_id}?msg=${c.message_id}`)}
            className="inline-flex items-center justify-center align-baseline rounded-full bg-primary/15 text-primary text-[10px] font-bold h-[18px] min-w-[18px] px-1 mx-0.5 hover:bg-primary/25 transition-colors"
            title={c.snippet}
          >
            {c.n}
          </button>
        );
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <div className="flex flex-col h-full" data-testid="ask-job-panel">
      <div className="flex-1 overflow-y-auto px-1 pb-3 space-y-3">
        {turns.length === 0 && (
          <div className="text-center py-12 px-6">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-3">
              <Sparkles className="h-6 w-6" />
            </div>
            <div className="font-display font-semibold">{t("ask_job_title")}</div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">{t("ask_job_hint")}</p>
          </div>
        )}
        {turns.map((turn, i) => (
          turn.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 py-2.5 text-sm max-w-[85%]" data-testid="ask-job-question">
                {turn.content}
              </div>
            </div>
          ) : (
            <Card key={i} className="p-3.5 max-w-[95%]" data-testid="ask-job-answer">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-primary mb-1.5">
                <Sparkles className="h-3.5 w-3.5" /> {t("ask_job_title")}
              </div>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{renderAnswer(turn)}</div>
              {(turn.citations || []).length > 0 && (
                <div className="mt-3 pt-2.5 border-t space-y-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("ask_job_sources")}</div>
                  {turn.citations.slice(0, 6).map((c) => (
                    <button
                      key={c.n}
                      type="button"
                      data-testid={`ask-job-source-${c.n}`}
                      onClick={() => navigate(`/chat/${c.conversation_id}?msg=${c.message_id}`)}
                      className="w-full flex items-start gap-1.5 text-left text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <CornerDownRight className="h-3 w-3 mt-0.5 shrink-0 text-primary" />
                      <span className="min-w-0 truncate">
                        <span className="font-semibold text-primary">[{c.n}]</span> {c.sender_name}: {c.snippet}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          )
        ))}
        {loading && (
          <Card className="p-3.5 max-w-[95%]" data-testid="ask-job-thinking">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4 text-primary animate-pulse" /> {t("ask_job_thinking")}
            </div>
          </Card>
        )}
        <div ref={endRef} />
      </div>
      <div className="flex items-end gap-1.5 border-t pt-2.5 sticky bottom-0 bg-background">
        <Textarea
          data-testid="ask-job-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              ask();
            }
          }}
          placeholder={t("ask_job_placeholder")}
          rows={1}
          className="flex-1 resize-none min-h-[46px] max-h-28 rounded-xl text-sm"
        />
        <Button
          data-testid="ask-job-send"
          onClick={ask}
          disabled={!question.trim() || loading}
          size="icon"
          className="h-[46px] w-[46px] shrink-0 rounded-xl"
        >
          <Send className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
